import { getAlgoliaFacets } from "@algolia/autocomplete-preset-algolia";
import { RenderListItem } from "../RefinementListFilter/RefinedListValues";
import { GetSources } from "@algolia/autocomplete-core";
import {
  AddFacetFilter,
  CurrentFacetsFilters,
  CurrentFacetsFiltersResult
} from "./useAutocompleteSearch";
import { Language, SanityCode } from "src/utils/localization/languages/types";
import { localizedFacetSource } from "src/components/Algolia/utils";
import { LiteClient as SearchClient } from "algoliasearch/lite";

export const facetSources = [
  "destinationNames",
  "countryNames",
  "portNames",
  "cruiseTagsTree"
] as const;

export type PublicFacetSource = (typeof facetSources)[number];

export type FacetSource =
  | `${Exclude<PublicFacetSource, "portNames" | "cruiseTagsTree">}.${SanityCode}`
  | `cruiseTagsTree.${SanityCode}.level1`
  | "portNames";
type FacetSourceConfig = { maxFaceHits: number; showOnOpen: boolean };

const sourcesConfigMap: Record<PublicFacetSource, FacetSourceConfig> = {
  destinationNames: { maxFaceHits: 30, showOnOpen: true },
  countryNames: { maxFaceHits: 10, showOnOpen: false },
  portNames: { maxFaceHits: 10, showOnOpen: false },
  cruiseTagsTree: { maxFaceHits: 10, showOnOpen: false }
};

type Args = {
  searchClient: SearchClient;
  indexName: string;
  addFacetFilter: AddFacetFilter;
  currentFacetsFilters: CurrentFacetsFilters;
  enabledSources: readonly PublicFacetSource[];
  language: Language;
};

type BuildSources = (a: Args) => ReturnType<GetSources<RenderListItem>>;

export const buildSources: BuildSources = ({
  enabledSources,
  searchClient,
  indexName,
  addFacetFilter,
  currentFacetsFilters,
  language
}) => {
  const facetSourceFor = buildOneSource({
    searchClient,
    indexName,
    addFacetFilter,
    language
  });

  return enabledSources.map((sourceName) => {
    const config = sourcesConfigMap[sourceName];
    const showOnOpen = enabledSources.includes("destinationNames")
      ? config.showOnOpen
      : true;

    return facetSourceFor(
      localizedFacetSource(language, sourceName),
      { ...config, showOnOpen },
      () => currentFacetsFilters([sourceName])
    )[0];
  });
};

type BuildOneSourceArgs = Omit<Args, "currentFacetsFilters" | "enabledSources">;
const buildOneSource =
  ({ searchClient, indexName, addFacetFilter }: BuildOneSourceArgs) =>
  (
    facet: FacetSource,
    facetConfig: FacetSourceConfig,
    currentFacetsFilters: () => CurrentFacetsFiltersResult
  ): ReturnType<GetSources<RenderListItem>> => {
    const { showOnOpen, maxFaceHits } = facetConfig;

    return [
      {
        sourceId: facet,
        onSelect: async ({ item, setQuery, setIsOpen, refresh }) => {
          addFacetFilter(facet, item.label);
          setQuery("");
          setIsOpen(true);
          await refresh();
        },
        getItems: ({ query }) => {
          const { filter, facets } = currentFacetsFilters();

          if (hideResultsIfNotShowOnOpen(showOnOpen, filter, query)) return [];

          return getAlgoliaFacets<RenderListItem>({
            searchClient,
            transformResponse({ facetHits }) {
              const valuesForFacet = facets[facet] || [];
              const excludeAlreadySelected =
                buildExcludeAlreadySelected(valuesForFacet);

              return facetHits
                .flatMap((x) => x)
                .filter((f) => excludeAlreadySelected(f.label));
            },
            queries: [
              {
                indexName,
                facet: facet,
                type: "facet",
                params: {
                  filters: filter,
                  //@ts-expect-error looks like the types are wrong here
                  facetQuery: query,
                  maxFacetHits: maxFaceHits
                }
              }
            ]
          });
        }
      }
    ];
  };

const sourcesNotShowOnOpen = facetSources.filter((x) => {
  const config = sourcesConfigMap[x];
  return !config.showOnOpen;
});

const hideResultsIfNotShowOnOpen = (
  showOnOpen: boolean,
  currentFacetsFilters: string,
  query: string
) => {
  const alreadyFiltered = sourcesNotShowOnOpen.some((f) =>
    currentFacetsFilters.includes(f)
  );

  return !(showOnOpen || query != "" || alreadyFiltered);
};

const buildExcludeAlreadySelected =
  (valuesForFacet: string[]) =>
  (value: string): boolean => {
    return !valuesForFacet.includes(value);
  };
